import { useMutation } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { useContext, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { StandardErrorToast, Toast } from 'design-system/src/components/Toast/Toast';

import {
  client,
  NewCommentFile,
  FormFilesDocumentTypes,
  TaskComment,
  TaskEvent,
  OutboundTaskTypeKey,
  OutboundTaskType,
} from '../../../../api';
import type { Task, WithAdditionalTaskResponseFields } from '../../types';
import { TaskContext } from '../../contexts';
import { RenderHtmlString } from 'design-system/src/components/RenderHtmlString/RenderHtmlString';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import DeclineModal from '../../DeclineModal';
import NewFollowupTaskModal from '../../NewFollowupTaskModal';
import AppealRequestModal, { AdditionalInput } from '../../AppealRequestModal';
import { TaskInstructionSummary, TaskCompletionEventBanner } from '../../shared';
import { getBenefitTypeLabel } from '../../utils';
import { ActionCard } from '../../../ActionCard';
import { AttachedFile } from '../../Files';
import { OtherRequestButton } from '../../actions/shared/OtherRequestButton';
import { sponsoredTaskCopy } from '../../../businessRelationshipNotices/copy';
import { StructuredTaskViewWrapper } from '../StructuredTaskViewWrapper';

type ModalType = 'decline' | 'initiate' | 'other_request';

/**
 * Dedicated task view for PA denied - Confirm next step (PA_DENIAL_NEXT_STEPS).
 */
const PaDenialNextStepsTaskView: React.FC<{
  taskResponse: WithAdditionalTaskResponseFields<Task>;
}> = ({ taskResponse }) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [modalState, setModalState] = useState<{ show: boolean; type: ModalType }>({
    show: false,
    type: 'decline',
  });
  const [newFilesUploaded, setNewFilesUploaded] = useState<NewCommentFile[]>([]);
  const [additionalInput, setAdditionalInput] = useState<AdditionalInput>({
    needs_draft_review: false,
  });
  const [inputValue, setInputValue] = useState('');

  const { taskId, onTaskUpdate } = useContext(TaskContext);
  const navigate = useNavigate();

  const { drug, patient, prescriber } = taskResponse;
  const task = taskResponse.task;

  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';

  const closeModal = () => setModalState((prev) => ({ ...prev, show: false }));

  const initiate = useMutation({
    mutationFn: async () => {
      if (isTrainingMode) {
        console.warn(`Training Mode: Mocking "initiate" for task ID: ${taskId}`);
        return;
      }

      const formData = new FormData();
      formData.append('task_response', 'initiate');
      formData.append('message', inputRef.current?.value || '');
      formData.append('additional_input', JSON.stringify(additionalInput));

      if (newFilesUploaded.length) {
        const formFilesDocumentTypes: FormFilesDocumentTypes = {};
        newFilesUploaded.forEach((fileInfo) => {
          formData.append('files[]', fileInfo.file);
          formFilesDocumentTypes[fileInfo.file.name] = fileInfo.document_type;
        });
        formData.append('form_files_document_types', JSON.stringify(formFilesDocumentTypes));
      }

      await client.post(`tasks/${taskId}/respond`, formData);
    },
    onSuccess: () => {
      onTaskUpdate();
      toast.custom(() => <Toast variant="success">Task initiated.</Toast>);
      navigate('/tasks');
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
    },
  });

  const extraData: { label: string; value: string }[] = [];
  const benefitTypeLabel = getBenefitTypeLabel(task);
  if (benefitTypeLabel) {
    extraData.push({ label: 'Benefit type', value: benefitTypeLabel });
  }

  const isCompleted = task.status === 'completed';
  const initialComment = task.timeline?.[0] as TaskComment | undefined;
  const lastTimelineItem =
    isCompleted && task.timeline && task.timeline.length > 1 ? task.timeline[task.timeline.length - 1] : undefined;
  const completionEvent = lastTimelineItem?.item_type === 'event' ? (lastTimelineItem as TaskEvent) : undefined;

  return (
    <>
      <StructuredTaskViewWrapper
        taskType={(task.task_title || task.type) as OutboundTaskType}
        generalInfoTableProps={{
          patientName: drug.patient_name,
          prescriberName: drug.prescriber_name,
          dob: drug.dob,
          prescribedOn: drug.prescription_recieved_at,
          drugName: drug.name,
          prescriptionId: drug.id,
          extraData,
        }}
        taskCreatedAt={task.created_at}
        isCompleted={isCompleted}
        showBusinessRelationshipSmallPrint
        sidebar={{
          drug: {
            data: drug,
            showBusinessRelationshipNotice: task.is_pharma_contract_task,
            businessRelationshipNoticeCopy: sponsoredTaskCopy,
          },
          patient: patient ?? undefined,
          prescriber: prescriber ?? undefined,
        }}
      >
        {/* Completion message for completed tasks */}
        {isCompleted && <TaskCompletionEventBanner completionEvent={completionEvent} />}

        {/* Instructions */}
        <div className="mb-6 flex flex-col gap-4">
          <TaskInstructionSummary>
            Please confirm if you'd like Tandem to continue with appeals & enrollment:
          </TaskInstructionSummary>
          {initialComment && initialComment.item_type === 'comment' && (
            <>
              <Typography styledAs="bodyExtraSmallDMSans">
                <RenderHtmlString>{initialComment.message}</RenderHtmlString>
              </Typography>
              {initialComment.attachment_document_ids && initialComment.attachment_document_ids.length > 0 && (
                <div className="mt-2 flex flex-col gap-2">
                  {initialComment.attachment_document_ids.map((fileId) => (
                    <AttachedFile key={fileId} fileId={fileId} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <ActionCard>
          <div className="mb-4 flex flex-col gap-2 border-b-grayscale-100 pb-4 border-b-solid">
            <Typography styledAs="bodyDMSans" weight="bold">
              Confirm next steps
            </Typography>
            <Typography styledAs="bodySmallDMSans">
              Tandem can initiate the appeal process by drafting an appeal letter for you to review. Patient will also
              be enrolled in Bridge or Patient Assistance (PAP) program, if available.
            </Typography>
          </div>
          {isCompleted ? (
            <Typography styledAs="bodySmallSpaceGrotesk" color="var(--grayscale-600)">
              This action was confirmed with this task's completion.
            </Typography>
          ) : (
            <ButtonRow align={Alignment.SpaceBetween} fullWidth>
              <ButtonRow align={Alignment.Left}>
                <Button variant="tertiary" size="sm" onClick={() => setModalState({ show: true, type: 'decline' })}>
                  Cancel prescription
                </Button>
                <OtherRequestButton
                  setShowNewFollowupTaskModal={() => setModalState({ show: true, type: 'other_request' })}
                />
              </ButtonRow>
              <Button variant="primary" size="sm" onClick={() => setModalState({ show: true, type: 'initiate' })}>
                Initiate appeal
              </Button>
            </ButtonRow>
          )}
        </ActionCard>
      </StructuredTaskViewWrapper>

      {modalState.show && modalState.type === 'decline' && (
        <DeclineModal
          closeModal={closeModal}
          patient={patient}
          drug={drug}
          taskKey={task.task_key as OutboundTaskTypeKey}
          taskId={taskId}
          onTaskUpdate={onTaskUpdate}
        />
      )}

      {modalState.show && modalState.type === 'initiate' && (
        <AppealRequestModal
          closeModal={closeModal}
          initiate={initiate}
          inputRef={inputRef}
          patient={patient}
          drug={drug}
          task={task}
          inputValue={inputValue}
          setInputValue={setInputValue}
          files={newFilesUploaded}
          onFilesChange={setNewFilesUploaded}
          setAdditionalInput={setAdditionalInput}
          additionalInput={additionalInput}
          appealRequestModalType="initiate_with_next_steps"
        />
      )}

      {modalState.show && modalState.type === 'other_request' && (
        <NewFollowupTaskModal
          closeModal={closeModal}
          patient={patient}
          drug={drug}
          patientId={patient.id}
          prescriptionId={drug.id}
          taskId={taskId}
          relatedTaskKey={task.task_key as OutboundTaskTypeKey}
          relatedTaskType={task.type as OutboundTaskType}
        />
      )}
    </>
  );
};

export default PaDenialNextStepsTaskView;
